import { useEffect, useState } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';
import { InfoTip } from './InfoTip';

// Public / private choice inside an opened task (task page, task modal).
// Only the task's creator, its assignee and the PMO can change it
// (`canManage`, from GET /tasks/:id); others see the current state. Saved
// immediately and logged in the project timeline. A private task shows a
// closed padlock on its card and can't be opened by other project members.
export function TaskPrivacyField({ taskId, isPrivate, canManage, onChanged }: {
  taskId: number;
  isPrivate: boolean;
  canManage: boolean;
  onChanged?: (isPrivate: boolean) => void;
}) {
  const { t } = useT();
  const [value, setValue] = useState(isPrivate);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(isPrivate); }, [isPrivate]);

  const choose = async (next: boolean) => {
    if (next === value || !canManage) return;
    setSaving(true);
    try {
      await api.setTaskPrivacy(taskId, next);
      setValue(next);
      onChanged?.(next);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="task-privacy-field">
      <div className="selectable-group">
        <button type="button" className={`selectable${!value ? ' active' : ''}`} disabled={!canManage || saving}
          onClick={() => choose(false)}>
          <Unlock size={12} /> {t('tasks.publicTask')}
        </button>
        <button type="button" className={`selectable${value ? ' active' : ''}`} disabled={!canManage || saving}
          onClick={() => choose(true)}>
          <Lock size={12} /> {t('tasks.privateTaskShort')}
        </button>
      </div>
      <InfoTip align="end">{!canManage ? t('tasks.privacyReadOnly') : value ? t('tasks.privateTaskHelp') : t('tasks.publicTaskHelp')}</InfoTip>
    </div>
  );
}
